import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject, input } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { TabHeaderComponent } from "@app/shared/components/tab-header/tab-header.component";
import { DisabledControlDirective } from "@app/shared/directives/disabled-control/disabled-control.directive";
import { IProjectTab } from "@app/shared/interfaces/iproject-tab";
import { ButtonDirective } from "@progress/kendo-angular-buttons";
import { DatePickerComponent } from "@progress/kendo-angular-dateinputs";
import { LoaderComponent, LoaderSize, LoaderThemeColor, LoaderType, } from "@progress/kendo-angular-indicators";
import { TooltipDirective } from "@progress/kendo-angular-tooltip";
import { SVGIcon, cancelIcon, saveIcon } from "@progress/kendo-svg-icons";
import { AgGridAngular } from "ag-grid-angular";
import moment from "moment";
import { PlanningTaskData, SurfaceDevelopmentPlanningData } from "../../models/sd-planning-data";
import { GeneralProjectService } from "../../services/general-project.service";
import { SurfaceDevScheduleTaskPlanningGridOptions } from "./app-schedule-task-surfacedev-planning.gridoptions";

@Component({
  selector: 'app-schedule-task-surfacedev-planning',
  templateUrl: './app-schedule-task-surfacedev-planning.component.html',
  styleUrls: ['./app-schedule-task-surfacedev-planning.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
    ButtonDirective,
    DatePickerComponent,
    DisabledControlDirective,
    FormsModule,
    LoaderComponent,
    ReactiveFormsModule,
    TabHeaderComponent,
    TooltipDirective,
  ],
})
export class AppScheduleTaskSurfacedevPlanningComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder);
  private generalProjectService = inject(GeneralProjectService);
  private cdr = inject(ChangeDetectorRef);

  public loading = false;
  public saving = false;
  public editedTasks: Map<number, PlanningTaskData> = new Map<any, PlanningTaskData>();

  private gridApi;
  public gridOptions: SurfaceDevScheduleTaskPlanningGridOptions;

  public icons = {
    cancel: <SVGIcon>cancelIcon,
    save: <SVGIcon>saveIcon
  };

  public loader = {
    type: <LoaderType>'converging-spinner',
    themeColor: <LoaderThemeColor>'info',
    size: <LoaderSize>'small'
  };

  public planningData: SurfaceDevelopmentPlanningData;
  public projectPlanningForm: UntypedFormGroup;

  readonly projectTab = input<IProjectTab>(undefined);

  constructor() {

    this.createForm();
    this.gridOptions = new SurfaceDevScheduleTaskPlanningGridOptions();
  }

  public createForm() {
    this.projectPlanningForm = this.formBuilder.group({
      ProjectId: [null],
      GroupSpudDate: [null],
      GroupRigRelease: [null],
      StartFrac: [null],
      EarliestPOP: [null],
      EndFrac: [null],
      FormPlanningTasks: this.formBuilder.array([]),
      PlanningTasks: this.formBuilder.array([])
    });
  }

  get formPlanningTasks(): UntypedFormArray {
    return this.projectPlanningForm.get('FormPlanningTasks') as UntypedFormArray;
  }

  async ngOnInit() {
    this.loadPlanningTasks();
  }

  public loadPlanningTasks() {
    this.loading = true;
    this.cdr.detectChanges();

    this.generalProjectService.getSurfaceDevelopmentProjectPlanningData(this.projectTab().ProjectId).then((resp) => {
      this.planningData = resp;

      this.actualizeDates(this.planningData, ['GroupSpudDate', 'GroupRigRelease', 'StartFrac', 'EndFrac', 'EarliestPOP']);
      this.actualizeArrayDates(this.planningData.PlanningTasks, ['ActualCompleteDate', 'ActualStartDate', 'TargetCompletionDate']);

      this.projectPlanningForm.patchValue(this.planningData);

      const planningTaskArray = this.formBuilder.array(this.planningData.PlanningTasks.map(x => this.formBuilder.group(x)));
      this.projectPlanningForm.setControl('PlanningTasks', planningTaskArray);

      this.gridApi.setGridOption('rowData', this.projectPlanningForm.get('PlanningTasks').value);

      this.loading = false;
      this.saving = false;
      this.cdr.detectChanges();
    });
  }

  async onGridReady(params) {
    this.gridApi = params.api;
  }

  async onCellValueChanged(e) {
    if ((e.oldValue == null || e.oldValue === undefined) && (e.newValue == null || e.newValue === undefined)) return;

    if (e.data.ActualCompleteDate != null)
      e.data.ActualCompleteDate = moment(e.data.ActualCompleteDate).toDate();

    this.editedTasks.set(e.node.id, e.data);
    this.gridApi.applyTransaction({ update: [e.data] });
  }

  public savePlanningTasks() {
    this.saving = true;
    this.loading = true;
    this.cdr.detectChanges();

    let editedData = this.projectPlanningForm.get('FormPlanningTasks').value.concat(Array.from(this.editedTasks.values()));

    let dataToSave = {
      ProjectId: this.projectTab().ProjectId,
      PlanningTasks: editedData
    };

    this.generalProjectService.updateProjectPlanningTasks(dataToSave).subscribe(() => {
      this.refreshData();
    });
  }

  public cancelChanges(): void {
    this.refreshData();
  }

  async refreshData() {
    this.editedTasks.clear();
    this.loadPlanningTasks();
  }

  private actualizeDates(x: any, fields: Array<string>) {
    fields.forEach(field => {
      x[field] = x[field] != null ? moment(x[field]).startOf('day').toDate() : null;
    });
  }

  private actualizeArrayDates(x: any, fields: Array<string>) {
    fields.forEach(field => {
      x.forEach(element => {
        element[field] = element[field] != null ? moment(element[field]).startOf('day').toDate() : null;
      });
    });
  }
}